import { supabase } from './supabase';

// --- Reservierungen ---
export async function getReservierungen() {
  const SEITENGROESSE = 1000;
  let alle = [];
  let von = 0;
  while (true) {
    const { data, error } = await supabase
      .from('reservierungen')
      .select('*')
      .order('datum', { ascending: true })
      .range(von, von + SEITENGROESSE - 1);
    if (error) { console.error(error); break; }
    alle = alle.concat(data || []);
    if (!data || data.length < SEITENGROESSE) break;
    von += SEITENGROESSE;
  }
  return alle;
}

export async function addReservierung(r) {
  // kein .select(): Kunden ohne Login dürfen einfügen, aber nicht lesen (RLS) – RETURNING würde sonst scheitern
  const { error } = await supabase.from('reservierungen').insert([r]);
  if (error) throw error;
  return { ...r };
}

export async function updateReservierung(id, updates) {
  const { error } = await supabase.from('reservierungen').update(updates).eq('id', id);
  if (error) throw error;
}

export async function deleteReservierung(id) {
  const { error } = await supabase.from('reservierungen').delete().eq('id', id);
  if (error) throw error;
}

export async function addAnfrage(a) {
  const { error } = await supabase.from('anfragen').insert([a]);
  if (error) throw error;
  return { ...a };
}

export async function getReservierungenByEmail(email) {
  const { data } = await supabase
    .from('reservierungen')
    .select('id, datum, uhrzeit, personen, tisch_id, bereich, sitzbereich, status, bemerkung')
    .eq('email', email)
    .order('datum', { ascending: false })
    .limit(10);
  return data || [];
}

export async function getTischBelegung(datum) {
  const { data } = await supabase
    .from('belegung')
    .select('tisch_id, uhrzeit')
    .eq('datum', datum);
  return data || [];
}

// --- Bestellungen ---
export async function getBestellungen() {
  const { data, error } = await supabase
    .from('bestellungen')
    .select('*')
    .order('created_at', { ascending: false });
  if (error) console.error(error);
  return data || [];
}

export async function addBestellung(b) {
  const { data, error } = await supabase.from('bestellungen').insert([b]).select();
  if (error) throw error;
  return data[0];
}

export async function updateBestellung(id, updates) {
  const { error } = await supabase.from('bestellungen').update(updates).eq('id', id);
  if (error) throw error;
}

// --- Mitglieder ---
export async function getMitglieder() {
  const { data, error } = await supabase
    .from('mitglieder')
    .select('*')
    .order('created_at', { ascending: false });
  if (error) console.error(error);
  return data || [];
}

export async function addMitglied(m) {
  const { data, error } = await supabase.from('mitglieder').insert([m]).select();
  if (error) throw error;
  return data[0];
}

// --- Speisekarte ---
export async function getSpeisekarte() {
  const { data, error } = await supabase
    .from('speisekarte')
    .select('*')
    .order('kategorie', { ascending: true });
  if (error) console.error(error);
  return data || [];
}

export async function addSpeise(s) {
  const { data, error } = await supabase.from('speisekarte').insert([s]).select();
  if (error) throw error;
  return data[0];
}

export async function updateSpeise(id, updates) {
  const { error } = await supabase.from('speisekarte').update(updates).eq('id', id);
  if (error) throw error;
}

export async function deleteSpeise(id) {
  const { error } = await supabase.from('speisekarte').delete().eq('id', id);
  if (error) throw error;
}

// --- FAQ ---
export async function getFaqs(nurAktiv = true) {
  let q = supabase.from('faq').select('*').order('reihenfolge', { ascending: true }).order('created_at', { ascending: true });
  if (nurAktiv) q = q.eq('aktiv', true);
  const { data, error } = await q;
  if (error) console.error(error);
  return data || [];
}

export async function saveFaq(faq) {
  if (faq.id) {
    const { error } = await supabase.from('faq').update({ frage: faq.frage, antwort: faq.antwort, reihenfolge: faq.reihenfolge, aktiv: faq.aktiv }).eq('id', faq.id);
    if (error) throw error;
  } else {
    const { error } = await supabase.from('faq').insert({ frage: faq.frage, antwort: faq.antwort, reihenfolge: faq.reihenfolge ?? 0, aktiv: true });
    if (error) throw error;
  }
}

export async function deleteFaq(id) {
  const { error } = await supabase.from('faq').delete().eq('id', id);
  if (error) throw error;
}

// --- QR Stempel ---
export async function generateQrToken(typ) {
  const token = crypto.randomUUID();
  const ablauf = new Date(Date.now() + 10 * 60 * 1000).toISOString();
  const { data, error } = await supabase
    .from('qr_tokens')
    .insert({ typ, token, ablauf_am: ablauf })
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function validateQrToken(token) {
  const { data, error } = await supabase.from('qr_tokens').select('*').eq('token', token).single();
  if (error || !data) return { ok: false, fehler: 'Token nicht gefunden.' };
  if (data.eingeloest) return { ok: false, fehler: 'Dieser Stempel wurde bereits eingelöst.' };
  if (new Date(data.ablauf_am) < new Date()) return { ok: false, fehler: 'QR-Code abgelaufen. Bitte neuen Code anfordern.' };
  return { ok: true, data };
}

export async function redeemToken(token, userId) {
  const { ok, fehler, data: tokenData } = await validateQrToken(token);
  if (!ok) throw new Error(fehler);
  // Einlösen nur, wenn der Token in diesem Moment noch offen ist – zwei gleichzeitige Scans können so nicht beide durchgehen
  const { data: geaendert, error: updErr } = await supabase.from('qr_tokens').update({ eingeloest: true, eingeloest_von: userId }).eq('id', tokenData.id).eq('eingeloest', false).select('id');
  if (updErr) throw updErr;
  if (!geaendert || geaendert.length === 0) throw new Error('Dieser Stempel wurde gerade schon eingelöst.');
  const { error } = await supabase.from('stempel').insert({ user_id: userId, typ: tokenData.typ, token_id: tokenData.id });
  if (error) throw error;
}

export async function getStempel(userId) {
  const { data } = await supabase
    .from('stempel')
    .select('*')
    .eq('user_id', userId)
    .order('erstellt_am', { ascending: false });
  return data || [];
}
